

























import { Doughnut } from 'vue-chartjs/legacy';
import { ReportSpec } from '@/components/ReportControls.vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Vue from 'vue';
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiAutorenew, mdiPartyPopper } from '@mdi/js';
import { ArcElement, CategoryScale, Chart as ChartJS, Legend, registerables, Title, Tooltip, } from 'chart.js';
import axios from 'axios';
import { DateTime } from "luxon";

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    ArcElement,
    CategoryScale,
    ChartDataLabels,
    ...registerables,
);

export interface IncidentType {
  name: string;
  id: string;
  count: number;
  jobs: [
    {
      AccountCode: string;
      AccountName: string;
      TrackingId: string;
      at: Date;
      by_id: number;
      incident_type_id: number
      name: string;
    }
  ];
}

export default Vue.extend({
  name: 'DoughnutChart',
  components: {
    Doughnut,
    SvgIcon,
  },
  props: {
    chartId: {
      type: String,
      default: 'incident-chart',
    },
    datasetIdKey: {
      type: String,
      default: 'label',
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 270,
    },
    spec: Object as () => ReportSpec,
  },
  computed: {
    dataUrl(): string {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      if (this.spec?.dateStyle == "calendar") {
        const { depot, from, to } = this.spec;
        return `${BASE_URL}/reports/customer-review-incidents/${depot}/${DateTime.fromJSDate(from).toISODate()}/${DateTime.fromJSDate(to).toISODate()}/`;
      } else {
        const { depot, from, to } = this.spec;
        return `${BASE_URL}/reports/customer-review-incidents/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
      }
    },
  },
  data() {
    return {
      incidentCount: 0,
      incidentsList: [],
      spinPath: mdiAutorenew,
      celebPath: mdiPartyPopper,
      spinSize: 48,
      celebSize: 16,
      loading: true,
      chartData: {
        labels: [] as string[],
        datasets: [
          {
            backgroundColor: ['#800080', '#e69e04', '#00D8FF', '#DD1B16', '#808000', '#FFC0CB'],
            data: {},
            datalabels: {
              anchor: 'center',
              align: 'center',
              textAlign: 'center',
              offset: 0,
              clamp: true,
              backgroundColor: '#82BD53',
              borderColor: '#0B234E',
              borderWidth: 1,
              color: '#0B234E',
              borderRadius: 4,
              padding: {
                top: 2,
                bottom: 1,
                left: 3,
                right: 4,
              },
              font: {
                size: 18,
              },
              formatter: (value: any) => {
                return value.value > 0 ? value.value : null;
              },
            },
          },
        ],
      },
      chartOptions: {
        resizeDelay: 2,
        responsive: false,
        maintainAspectRatio: false,
        radius: '80%',
        layout: {
          padding: {
            top: 10,
            bottom: 20,
            left: 20,
            right: 20,
          },
        },
        plugins: {
          datalabels: {
            display: true,
          },
          title: {
            display: true,
            position: 'top',
            align: 'center',
            text: 'Incident Report',
            padding: {
              top: 1,
              bottom: 1,
            },
            font: {
              size: 16,
            },
          },
          legend: {
            display: true,
            position: 'right',
            labels: {
              boxWidth: 12,
              font: {
                size: 14,
              },
              padding: 3,
            },
          },
          tooltip: {
            backgroundColor: '#0B234E',
            borderColor: 'rgba(130, 189, 83, 80)',
            borderWidth: 1,
            caretPadding: 10,
            displayColors: false,
            xAlign: 'center',
            yAlign: 'center',
            position: 'nearest',
            titleAlign: 'center',
            titleMarginBottom: 0,
            titleSpacing: 0,
            footerAlign: 'right',
            footerSpacing: 1,
            bodySpacing: 0,
            padding: {
              top: 8,
              bottom: 8,
              left: 6,
              right: 10,
            },
            titleFont: {
              size: 12,
            },
            footerFont: {
              size: 11,
            },
            labelAlign: 'left',
            callbacks: {
              title: (context: any) => {
                const titleText: string = context[0].label;
                return titleText;
              },
              label: () => '', // Empty to allow for title + footer combo without default label getting inserted
              footer: (context: any) => {
                const incidentList = context[0].raw.incidents;
                const formattedList: string[] = [];
                incidentList.forEach((item: any) => {
                  const incDate = new Date(item.at).toLocaleDateString();
                  formattedList.push(`${item.AccountCode} - ${item.TrackingID} - ${incDate}`);
                });
                return formattedList.slice(0, 15);
              },
            },
          },
        },
      },
    };
  },
  watch: {
    spec: {
      immediate: true,
      async handler() {
        const response = await axios.get<{ data: IncidentType[], incident_count: number }>(this.dataUrl, {
          params: {
            account_codes: this.spec.accountCodes.flatMap((a) => a.codes),
          },
        });
        this.chartData.labels = response.data.data.map((x: IncidentType) => x.name);

        this.chartData.datasets[0].data = response.data.data.map((item: IncidentType) => ({
          value: item.count,
          incidents: item.jobs,
        }));
        this.incidentCount = response.data.incident_count;
        this.loading = false;
      },
    },
  },
});

